import React from 'react';
import { Link } from 'react-router-dom';
import { useRenewalTime } from './useRenewalTime';
import { event } from '../utils/gtm';
import moment from 'moment-timezone';

const CreditUsageWidget = ({ user }) => {  
  // Call the custom hook unconditionally at the top of the component.
  const renewalTime = useRenewalTime(user?.credits_last_reset, Intl.DateTimeFormat().resolvedOptions().timeZone);

  // The early return is now placed *after* all hook calls, fixing the ESLint error.
  if (!user) {
    return null; // Don't render if no user data is available
  }

  // Perform all the necessary credit calculations
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const renewableCreditsTotal = (user.free_credits || 0) + (user.subscription_credits || 0); 
  const renewableCreditsLimit = user.plan === 'pro' ? 10 + 250 : 10;
  const percentageUsed = renewableCreditsLimit > 0 ? Math.min((renewableCreditsTotal / renewableCreditsLimit) * 100, 100) : 0;
  const totalSpendableCredits = (user.free_credits || 0) + (user.subscription_credits || 0) + (user.purchased_credits || 0);

  const isLowOnCredits = totalSpendableCredits < 5;

  return (
    // The outer container styling is removed, as it's now part of a larger panel.
    <div className="text-white h-full flex flex-col">
      <h3 className="font-bold text-lg mb-2">Available Credits</h3>
      
      <p className="text-4xl font-bold mb-4">{totalSpendableCredits}</p>

      {/* This container handles the flexible spacing of the content below */}
      <div className="flex-grow flex flex-col">
        {/* Renewable Credits Section */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <p className="text-sm text-gray-400">Renewable Credits Usage</p>
            <p className="text-sm font-medium">{renewableCreditsTotal} / {renewableCreditsLimit}</p>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2.5">
            <div 
              className="bg-blue-500 h-2.5 rounded-full" 
              style={{ width: `${percentageUsed}%` }}
            ></div>
          </div>
          {renewalTime && (
            <p className="text-xs text-gray-500 mt-1">
              Daily credits reset at:{' '}
              <span className="font-bold text-gray-400">{moment.tz(user.credits_last_reset, timezone).add(1, 'days').format('h:mm A')}</span>
             </p>
          )}
        </div>

        {/* Purchased Credits Section (conditional) */}
        {user.purchased_credits > 0 && (
          <p className="text-sm mt-4 text-gray-300">
            Purchased Credits: <span className="font-bold text-white">{user.purchased_credits}</span> (Never expire)
          </p>
        )}

        {/* Conditional Upsell Message */}
        {user.plan === 'free' && isLowOnCredits && (
          // mt-auto pushes this block to the bottom of the flex container
          <div className="mt-auto pt-4">
            <div className="text-sm text-center bg-blue-900/50 border border-blue-700 rounded-md p-2">
              <p>You're running low on credits!</p>
              <Link to="/pricing" className="font-bold text-blue-400 hover:underline" onClick={() => event({ action: 'click_upgrade_intent', category: 'Monetization', label: 'Source: Homepage Upsell Low Credits' })}>
                Upgrade to Pro for more credits.
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditUsageWidget;